import { addDays, startOfMonth, type Day } from '../core/calendar.ts';
import { abs, sub, sum, ZERO, type Decimal } from '../core/decimal.ts';
import type { Asset, Checkpoint, Id, Plan, PlanConfiguration, Transaction, TransactionType } from '../types/domain.ts';
import { assetFromDepreciatingAsset, assetFromProperty, assetValue, defaultsToAppreciation } from './asset.ts';
import { depreciatingAssetMonthlyCosts } from './depreciating-asset.ts';
import { propertyMonthlyCosts } from './property.ts';
import { monthlyEquivalentAmount } from './transaction.ts';
import { countsAsDebt } from '../calculations/net-worth.ts';

// MARK: Starting cash (R17)

/**
 * PlanConfiguration.balanceDate: the day `initialBalance` is the cash at the end of; without
 * `initialBalanceDate` the day before `windowStart`, so the balance is the cash as the window opens.
 */
export const balanceDate = (configuration: PlanConfiguration, windowStart: Day): Day =>
  configuration.initialBalanceDate ?? addDays(windowStart, -1);

/**
 * Plan.cashBaseline: where "cash so far" starts, the latest checkpoint's confirmed cash on its
 * date, else `initialBalance` at the end of its balance date. Metrics count only flows after `since`.
 */
export const cashBaseline = (
  plan: Plan,
  checkpoint: Checkpoint | null,
  windowStart: Day,
): { readonly cash: Decimal; readonly since: Day } =>
  checkpoint !== null
    ? { cash: checkpoint.confirmedBalances.cash, since: checkpoint.date }
    : { cash: plan.configuration.initialBalance, since: balanceDate(plan.configuration, windowStart) };

/** Plan.investmentBaseline (R18): the latest checkpoint's confirmed investments on its date, else null (modelled). */
export const investmentBaseline = (checkpoint: Checkpoint | null): { readonly investments: Decimal; readonly since: Day } | null =>
  checkpoint === null ? null : { investments: checkpoint.confirmedBalances.investments, since: checkpoint.date };

// MARK: Transactions by role

const isActiveDebt = (t: Transaction): boolean => t.type === 'debt' && t.isActive;

/** Plan.activeTransactions filtered to one type, in plan order. */
export const activeOfType = (plan: Plan, type: TransactionType): readonly Transaction[] =>
  plan.transactions.filter((t) => t.isActive && t.type === type);

/** Plan.debtTransactions. */
export const debtTransactions = (plan: Plan): readonly Transaction[] => activeOfType(plan, 'debt');

/** Plan.linkedTransactions(for:): active transactions of any type linked to the asset. */
export const linkedTransactions = (plan: Plan, assetId: Id): readonly Transaction[] =>
  plan.transactions.filter((t) => t.linkedAssetId === assetId && t.isActive);

const linkedOfType = (plan: Plan, assetId: Id, type: TransactionType): readonly Transaction[] =>
  plan.transactions.filter((t) => t.type === type && t.linkedAssetId === assetId && t.isActive);

export const linkedExpenses = (plan: Plan, assetId: Id) => linkedOfType(plan, assetId, 'expense');
export const linkedDebts = (plan: Plan, assetId: Id) => linkedOfType(plan, assetId, 'debt');

// MARK: Monthly equivalents

const monthlyTotal = (transactions: readonly Transaction[]): Decimal => sum(transactions.map(monthlyEquivalentAmount));

export const monthlyIncome = (plan: Plan): Decimal => monthlyTotal(activeOfType(plan, 'income'));
export const monthlyExpenses = (plan: Plan): Decimal => monthlyTotal(activeOfType(plan, 'expense'));
export const monthlyDebtPayments = (plan: Plan): Decimal => monthlyTotal(activeOfType(plan, 'debt'));
export const monthlyInvestments = (plan: Plan): Decimal => monthlyTotal(activeOfType(plan, 'investment'));

/** Legacy built-in running costs of active properties. */
export const monthlyPropertyCosts = (plan: Plan): Decimal =>
  sum(plan.properties.filter((p) => p.isActive).map(propertyMonthlyCosts));

/** Legacy running costs of active depreciating assets. */
export const monthlyAssetCosts = (plan: Plan): Decimal =>
  sum(plan.depreciatingAssets.filter((a) => a.isActive).map(depreciatingAssetMonthlyCosts));

/** income − expenses − debt payments − investments − property costs − asset costs, left to right. */
export const monthlyDisposable = (plan: Plan): Decimal =>
  [monthlyExpenses, monthlyDebtPayments, monthlyInvestments, monthlyPropertyCosts, monthlyAssetCosts]
    .map((part) => part(plan))
    .reduce(sub, monthlyIncome(plan));

/** totalMonthlyCostOfOwnership(for: Asset): every active linked transaction, whatever its type. */
export const monthlyCostOfOwnership = (plan: Plan, assetId: Id): Decimal => monthlyTotal(linkedTransactions(plan, assetId));

/** Plan.linkedMonthlyCost(for:): linked expenses, then linked debts. */
export const linkedMonthlyCost = (plan: Plan, assetId: Id): Decimal =>
  monthlyTotal([...linkedExpenses(plan, assetId), ...linkedDebts(plan, assetId)]);

// MARK: Debt lines

/** Plan.propertyIds: every unified property asset and legacy property; debts linked to them are mortgages. */
export const propertyIds = (plan: Plan): ReadonlySet<Id> =>
  new Set([...plan.assets.filter((a) => a.assetClass === 'property').map((a) => a.id), ...plan.properties.map((p) => p.id)]);

/** Plan.debtsCountedInNetWorth (also Plan.standaloneDebts): active debts in the debt line of net worth (R3). */
export function debtsCountedInNetWorth(plan: Plan, treatedAsDebt: ReadonlySet<Id> = new Set()): readonly Transaction[] {
  const ids = propertyIds(plan);
  return plan.transactions.filter((t) => isActiveDebt(t) && countsAsDebt(t, ids, treatedAsDebt));
}

/** Σ (debtCurrentBalance ?? debtPrincipal ?? 0) over the debts counted in net worth, signs as stored. */
export const standaloneDebtBalance = (plan: Plan): Decimal =>
  sum(debtsCountedInNetWorth(plan).map((d) => d.debtCurrentBalance ?? d.debtPrincipal ?? ZERO));

const ledgerBalance = (debts: readonly Transaction[]): Decimal => sum(debts.map((t) => abs(t.debtCurrentBalance ?? ZERO)));

/** Plan.ledgerDebtBalance: Σ |debtCurrentBalance ?? 0| over active debts, optionally only unlinked ones. */
export const ledgerDebtBalance = (plan: Plan, includeAssetLinked: boolean): Decimal =>
  ledgerBalance(plan.transactions.filter((t) => isActiveDebt(t) && (includeAssetLinked || t.linkedAssetId === null)));

/** Plan.ledgerDebtBalanceCountedInNetWorth: Σ |debtCurrentBalance ?? 0| over the debt line (R3). */
export const ledgerDebtBalanceCountedInNetWorth = (plan: Plan): Decimal => ledgerBalance(debtsCountedInNetWorth(plan));

// MARK: Asset values

/** Plan.activeAssets: active unified assets, then active legacy properties and depreciating assets not already present. */
export function activeAssets(plan: Plan): readonly Asset[] {
  const unified = plan.assets.filter((a) => a.isActive);
  const ids = new Set(unified.map((a) => a.id));
  return [
    ...unified,
    ...plan.properties.filter((p) => p.isActive && !ids.has(p.id)).map(assetFromProperty),
    ...plan.depreciatingAssets.filter((a) => a.isActive && !ids.has(a.id)).map(assetFromDepreciatingAsset),
  ];
}

/**
 * A sold item stops counting from the start of its sale month, the month its proceeds reach
 * cash (R2; Asset.isSold and the legacy sale checks).
 */
export const isSoldBy = (sale: { readonly date: Day } | null, date: Day): boolean =>
  sale !== null && date >= startOfMonth(sale.date);

/** Bought by the end of `date` and not sold: nothing counts before it exists (TIMELINE R23). */
export const isOwnedOn = (asset: { readonly purchaseDate: Day; readonly saleEvent: { readonly date: Day } | null }, date: Day): boolean =>
  !(date < asset.purchaseDate) && !isSoldBy(asset.saleEvent, date);

/** Plan.totalAssetValue(at:): Σ Asset.value over the activeAssets owned at `date`. */
export const totalAssetValue = (plan: Plan, date: Day): Decimal =>
  sum(activeAssets(plan).filter((a) => isOwnedOn(a, date)).map((asset) => assetValue(asset, date)));

/**
 * Plan.totalDepreciatingAssetEquity(at:): Σ Asset.value of the non-property activeAssets
 * (legacy depreciating assets converted, each once) not sold at `date`. A gross value: the
 * forecast's equity term and a check-in's asset value (R1, R10).
 */
export const totalDepreciatingAssetValue = (plan: Plan, date: Day): Decimal =>
  sum(
    activeAssets(plan)
      .filter((a) => !defaultsToAppreciation(a.assetClass) && isOwnedOn(a, date))
      .map((asset) => assetValue(asset, date)),
  );
